import { ChangeEvent, FC, useCallback, useMemo, useState } from 'react';

import { Field, FieldArray, FormikProvider, useFormik } from 'formik';
import { Plus, Send, Trash2 } from 'lucide-react';
import useSWRMutation from 'swr/mutation';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';

import css from './CreateCampaign.module.css';

import { mailingService } from 'v2/api/mailingService';
import { RichTextarea } from 'v2/components/richTextarea/RichTextarea';
import { MailingButton, MailingCampaignRequest } from 'v2/types/MailingModel';

interface CreateCampaignProps {
  onSuccess: () => void;
}

interface CampaignFormValues {
  messageText: string;
  buttons: MailingButton[];
  usernames: string;
}

export const CreateCampaign: FC<CreateCampaignProps> = ({ onSuccess }) => {
  const [previewSending, setPreviewSending] = useState(false);
  const [previewUsername, setPreviewUsername] = useState('');

  const { trigger: createCampaign, isMutating } = useSWRMutation(
    'createMailingCampaign',
    mailingService.createCampaign
  );

  const { trigger: sendPreview } = useSWRMutation('sendMailingPreview', mailingService.sendPreview);

  const formik = useFormik<CampaignFormValues>({
    initialValues: {
      messageText: '',
      buttons: [],
      usernames: '',
    },
    onSubmit: async (values) => {
      const form: MailingCampaignRequest = {
        messageText: values.messageText,
        buttons: values.buttons.length > 0 ? values.buttons : undefined,
        recipientUsernames: values.usernames
          .split('\n')
          .map((u) => u.trim())
          .filter(Boolean),
      };

      await createCampaign(form);
      onSuccess();
    },
  });

  const handleMessageTextChange = useCallback(
    (value: string) => {
      formik.setFieldValue('messageText', value);
    },
    [formik]
  );

  const handleSendPreview = async () => {
    if (!previewUsername.trim()) return;

    setPreviewSending(true);

    try {
      await sendPreview({
        messageText: formik.values.messageText,
        buttons: formik.values.buttons.length > 0 ? formik.values.buttons : undefined,
        telegramUsername: previewUsername.trim(),
      });
    } finally {
      setPreviewSending(false);
    }
  };

  const handleAddButton = () => {
    formik.setFieldValue('buttons', [...(formik.values.buttons || []), { text: '', url: '' }]);
  };

  const handleRemoveButton = (index: number) => () => {
    formik.setFieldValue('buttons', formik.values.buttons?.filter((_, i) => i !== index) || []);
  };

  const handlePreviewUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPreviewUsername(e.target.value);
  };

  const recipientCount = useMemo(() => {
    return formik.values.usernames.split('\n').filter((username) => username.trim()).length;
  }, [formik.values.usernames]);

  const canSubmit = formik.values.messageText && recipientCount > 0;
  const canSendPreview = formik.values.messageText && previewUsername.trim();

  return (
    <FormikProvider value={formik}>
      <form className={css.createCampaignLayout} onSubmit={formik.handleSubmit}>
        <div className={css.formContainer}>
          <div className={css.formSection}>
            <div className={css.sectionTitle}>1. Message Content</div>

            <RichTextarea
              label="Message Text"
              value={formik.values.messageText}
              onChange={handleMessageTextChange}
              required
            />

            <div className={css.buttonsSection}>
              <div className={css.buttonsSectionHeader}>
                <span className={css.buttonsSectionTitle}>Inline Buttons (optional)</span>
              </div>

              <FieldArray name="buttons">
                {({ form }) => (
                  <div className={css.buttonsList}>
                    {form.values.buttons?.map((_: MailingButton, index: number) => (
                      <div key={index} className={css.buttonItem}>
                        <Field
                          as={TextField}
                          name={`buttons.${index}.text`}
                          label="Button Text"
                          size="small"
                          sx={{ flex: 1 }}
                          required
                        />
                        <Field
                          as={TextField}
                          name={`buttons.${index}.url`}
                          label="Button URL"
                          size="small"
                          sx={{ flex: 1 }}
                          required
                        />
                        <IconButton size="small" color="error" onClick={handleRemoveButton(index)}>
                          <Trash2 size={16} />
                        </IconButton>
                      </div>
                    ))}

                    <Button size="small" variant="outlined" startIcon={<Plus size={14} />} onClick={handleAddButton}>
                      Add Button
                    </Button>
                  </div>
                )}
              </FieldArray>
            </div>
          </div>

          <div className={css.formSection}>
            <div className={css.sectionTitle}>2. Recipients (Telegram Usernames)</div>

            <TextField
              multiline
              rows={8}
              name="usernames"
              value={formik.values.usernames}
              onChange={formik.handleChange}
              placeholder={`Enter Telegram usernames, one per line:
username1
username2
username3`}
              className={css.usernamesTextarea}
              size="small"
            />
          </div>

          <div className={css.submitSection}>
            <Button type="submit" variant="contained" disabled={!canSubmit || isMutating} size="large">
              Create Campaign
            </Button>

            <span className={css.recipientCount}>
              Recipients: <span className={css.recipientCountHighlight}>{recipientCount}</span>
            </span>
          </div>
        </div>

        <div className={css.previewContainer}>
          <div className={css.previewHeader}>
            <span className={css.previewTitle}>Message Preview</span>
          </div>

          <div className={css.previewSendSection}>
            <TextField
              size="small"
              value={previewUsername}
              onChange={handlePreviewUsernameChange}
              placeholder="username"
              sx={{ flex: 1 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<Send size={14} />}
              onClick={handleSendPreview}
              disabled={previewSending || !canSendPreview}
            >
              Send Preview
            </Button>
          </div>

          <div className={css.previewPhone}>
            <div className={css.previewPhoneHeader}>
              <div className={css.previewPhoneAvatar}>🤖</div>
              <div className={css.previewPhoneName}>Campaign Bot</div>
            </div>

            <div className={css.previewMessages}>
              <div
                className={css.previewMessageBubble}
                dangerouslySetInnerHTML={{
                  __html: formik.values.messageText || '<i style="opacity: 0.5">Enter message text...</i>',
                }}
              />

              {formik.values.buttons.length > 0 && (
                <div className={css.previewButtons}>
                  {formik.values.buttons.map((btn, idx) => (
                    <a key={idx} href={btn.url || '#'} className={css.previewButton} target="_blank" rel="noreferrer">
                      {btn.text || 'Button'}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </FormikProvider>
  );
};
